import React, { useState, useEffect } from 'react';
import { AgentPrompt, PromptUpdate } from '../../types';
import './PromptEditor.css';

interface PromptEditorProps {
  prompt: AgentPrompt;
  onSave: (promptKey: string, updates: PromptUpdate) => Promise<void>;
  saving: boolean;
}

const PromptEditor: React.FC<PromptEditorProps> = ({ prompt, onSave, saving }) => {
  const [formData, setFormData] = useState<PromptUpdate>({
    name: prompt.name,
    description: prompt.description,
    template: prompt.template,
    variables: prompt.variables,
    category: prompt.category,
    model_config: prompt.model_config,
    is_active: prompt.is_active,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<'template' | 'settings' | 'preview'>('template');
  const [templateLines, setTemplateLines] = useState<number>(1);

  // Update form data when prompt changes
  useEffect(() => {
    setFormData({
      name: prompt.name,
      description: prompt.description,
      template: prompt.template,
      variables: prompt.variables,
      category: prompt.category,
      model_config: prompt.model_config,
      is_active: prompt.is_active,
    });
    setHasChanges(false);
    setTemplateLines(prompt.template.split('\n').length);
  }, [prompt]);

  // Track changes
  useEffect(() => {
    const changed = 
      formData.name !== prompt.name ||
      formData.description !== prompt.description ||
      formData.template !== prompt.template ||
      JSON.stringify(formData.variables) !== JSON.stringify(prompt.variables) ||
      formData.category !== prompt.category ||
      JSON.stringify(formData.model_config) !== JSON.stringify(prompt.model_config) ||
      formData.is_active !== prompt.is_active;
    
    setHasChanges(changed);
  }, [formData, prompt]);

  const handleTemplateChange = (value: string) => {
    setFormData(prev => ({ ...prev, template: value }));
    setTemplateLines(value.split('\n').length);
  };

  const handleVariablesChange = (value: string) => {
    try {
      const variables = value.split('\n').filter(v => v.trim()).map(v => v.trim());
      setFormData(prev => ({ ...prev, variables }));
    } catch (error) {
      console.error('Invalid variables format:', error);
    }
  };

  const handleModelConfigChange = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      model_config: {
        ...prev.model_config,
        [key]: value,
      }
    }));
  };

  const handleSave = async () => {
    try {
      await onSave(prompt.prompt_key, formData);
    } catch (error) {
      console.error('Failed to save prompt:', error);
    }
  };

  const handleReset = () => {
    setFormData({
      name: prompt.name,
      description: prompt.description,
      template: prompt.template,
      variables: prompt.variables,
      category: prompt.category,
      model_config: prompt.model_config,
      is_active: prompt.is_active,
    });
    setHasChanges(false);
  };

  const renderTemplate = () => {
    const lines = Array.from({ length: Math.max(templateLines, 10) }, (_, i) => i + 1);
    
    return (
      <div className="template-editor">
        <div className="editor-header">
          <h3>Template del Prompt</h3>
          <div className="template-stats">
            <span>{formData.template?.length || 0} caracteres</span>
            <span>{templateLines} líneas</span>
          </div>
        </div>
        
        <div className="editor-container">
          <div className="line-numbers">
            {lines.map(line => (
              <div key={line} className="line-number">{line}</div>
            ))}
          </div>
          
          <textarea
            className="template-textarea"
            value={formData.template || ''}
            onChange={(e) => handleTemplateChange(e.target.value)}
            placeholder="Escribe el template del prompt aquí..."
            spellCheck={false}
            rows={Math.max(templateLines, 10)}
          />
        </div>
        
        {formData.variables && formData.variables.length > 0 && (
          <div className="template-variables">
            <h4>Variables disponibles:</h4>
            <div className="variables-list">
              {formData.variables.map((variable, index) => (
                <span key={index} className="variable-tag">
                  {variable}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSettings = () => {
    return (
      <div className="settings-panel">
        <div className="settings-section">
          <h3>Información Básica</h3>
          
          <div className="form-group">
            <label>Nombre</label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Nombre del prompt"
            />
          </div>
          
          <div className="form-group">
            <label>Descripción</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descripción del prompt"
              rows={3}
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Categoría</label>
              <select
                value={formData.category || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              >
                <option value="core">Core</option>
                <option value="workflow">Workflow</option>
                <option value="system">System</option>
                <option value="customer">Customer</option>
              </select>
            </div>
            
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_active || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                <span>Activo</span>
              </label>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <h3>Variables</h3>
          <div className="form-group">
            <label>Variables (una por línea)</label>
            <textarea
              value={formData.variables?.join('\n') || ''}
              onChange={(e) => handleVariablesChange(e.target.value)}
              placeholder="user_name&#10;session_id&#10;context"
              rows={5}
            />
          </div>
        </div>

        <div className="settings-section">
          <h3>Configuración del Modelo</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Temperatura</label>
              <input
                type="number"
                min="0"
                max="2"
                step="0.1"
                value={formData.model_config?.temperature || 0.7}
                onChange={(e) => handleModelConfigChange('temperature', parseFloat(e.target.value))}
              />
            </div>
            
            <div className="form-group">
              <label>Max Tokens</label>
              <input
                type="number"
                min="1"
                max="4000"
                value={formData.model_config?.max_tokens || 1000}
                onChange={(e) => handleModelConfigChange('max_tokens', parseInt(e.target.value))}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Top P</label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={formData.model_config?.top_p || 1}
                onChange={(e) => handleModelConfigChange('top_p', parseFloat(e.target.value))}
              />
            </div>
            
            <div className="form-group">
              <label>Frequency Penalty</label>
              <input
                type="number"
                min="-2"
                max="2"
                step="0.1"
                value={formData.model_config?.frequency_penalty || 0}
                onChange={(e) => handleModelConfigChange('frequency_penalty', parseFloat(e.target.value))}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPreview = () => {
    const processedTemplate = formData.template || '';
    const variableRegex = /\{([^}]+)\}/g;
    const matches = Array.from(processedTemplate.matchAll(variableRegex));
    
    return (
      <div className="preview-panel">
        <div className="preview-header">
          <h3>Vista Previa</h3>
          <span className="preview-note">
            Las variables aparecen resaltadas en el template
          </span>
        </div>
        
        <div className="preview-content">
          <div
            className="preview-template"
            dangerouslySetInnerHTML={{
              __html: processedTemplate.replace(
                variableRegex,
                '<span class="preview-variable">{$1}</span>'
              ).replace(/\n/g, '<br>')
            }}
          />
        </div>
        
        {matches.length > 0 && (
          <div className="preview-variables">
            <h4>Variables encontradas en el template:</h4>
            <div className="variables-found">
              {Array.from(new Set(matches.map(match => match[1]))).map((variable, index) => (
                <span key={index} className="variable-found">
                  {variable}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="prompt-editor">
      {/* Header */}
      <div className="prompt-editor-header">
        <div className="prompt-info">
          <h2>{prompt.name}</h2>
          <div className="prompt-meta">
            <span className="prompt-key">{prompt.prompt_key}</span>
            <span className="prompt-version">Versión {prompt.version}</span>
            <span className={`prompt-status-pill ${prompt.is_active ? 'active' : 'inactive'}`}>
              {prompt.is_active ? 'Activo' : 'Inactivo'}
            </span>
          </div>
        </div>
        
        <div className="editor-actions">
          <button
            onClick={handleReset}
            disabled={!hasChanges || saving}
            className="reset-button"
          >
            Descartar
          </button>
          
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="save-button"
          >
            {saving ? (
              <>
                <div className="spinner-small"></div>
                Guardando...
              </>
            ) : (
              'Guardar Cambios'
            )}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="editor-tabs">
        <button
          className={`tab ${activeTab === 'template' ? 'active' : ''}`}
          onClick={() => setActiveTab('template')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
            <polyline points="14,2 14,8 20,8"/>
          </svg>
          Template
        </button>
        
        <button
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3"/>
            <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/>
          </svg>
          Configuración
        </button>
        
        <button
          className={`tab ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => setActiveTab('preview')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          Vista Previa
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'template' && renderTemplate()}
        {activeTab === 'settings' && renderSettings()}
        {activeTab === 'preview' && renderPreview()}
      </div>
    </div>
  );
};

export default PromptEditor;